import {
  useApiQueryClient,
  useComplexQuery,
  useMutation
} from '@sequencehq/api'
import { Member, useStytchB2BClient, useStytchMember } from '@stytch/react/b2b'
import { useNotifications } from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'

export const TEAM_SETTINGS_QUERY_KEY = 'teamSettings'

// Safety valve on cursor pagination: at the max page size of 1000 this covers
// 20,000 members, well beyond any real workspace, so it only ever bounds an
// unexpected non-terminating next_cursor rather than truncating real data.
const MAX_MEMBER_PAGES = 20

export const useTeamSettings = () => {
  const stytch = useStytchB2BClient()
  const { member: user } = useStytchMember()
  const { displayNotification } = useNotifications()

  const queryClient = useApiQueryClient()

  // Stytch's members.search returns a single page (default 100, max 1000 per
  // request) and signals more results via results_metadata.next_cursor. Follow
  // the cursor until it is null so accounts with more members than one page fit
  // (e.g. 160 members) show every member, not just the first page.
  const fetchAllMembers = async (
    cursor?: string,
    page = 1
  ): Promise<Member[]> => {
    const res = await stytch.organization.members.search({
      limit: 1000,
      ...(cursor ? { cursor } : {})
    })

    // results_metadata is always present on a real response; guard so a mock or
    // an unexpected shape degrades to a single page rather than throwing.
    const nextCursor = res.results_metadata?.next_cursor

    if (!nextCursor || page >= MAX_MEMBER_PAGES) {
      return res.members
    }

    return [...res.members, ...(await fetchAllMembers(nextCursor, page + 1))]
  }

  const {
    data: members,
    isLoading,
    isError
  } = useComplexQuery({
    queryFn: () => fetchAllMembers(),
    queryKey: [TEAM_SETTINGS_QUERY_KEY],
    staleTime: 1 * MINUTES
  })

  const inviteMutator = useMutation(
    dashboardv99990101Client.postSequenceAccountInvite,
    {
      onSuccess: (_data, { email }) => {
        displayNotification('Invitation sent', {
          description: `Invitation was sent to ${email}`,
          type: 'success'
        })

        queryClient.invalidateQueries({
          queryKey: [TEAM_SETTINGS_QUERY_KEY]
        })
      },
      onError() {
        displayNotification('Failed to send invite. Please try again.', {
          type: 'error'
        })
      }
    }
  )

  const updateMemberRolesMutator = useMutation(
    dashboardv99990101Client.putUpdateUserRoles,
    {
      onSuccess: () => {
        displayNotification('Member roles updated', {
          description: `Member roles were updated.`,
          type: 'success'
        })

        queryClient.invalidateQueries({
          queryKey: [TEAM_SETTINGS_QUERY_KEY]
        })
      },
      onError() {
        displayNotification(
          'Failed to update member roles. Please try again.',
          {
            type: 'error'
          }
        )
      }
    }
  )

  const deleteMember = async (member: Member) => {
    try {
      const response = await stytch.organization.members.delete(
        member.member_id
      )

      if (response.status_code === 200) {
        displayNotification('Successfully deleted user', {
          type: 'success',
          description: `User ${member.email_address} has been removed from the workspace.`
        })
        queryClient.invalidateQueries({
          queryKey: [TEAM_SETTINGS_QUERY_KEY]
        })
      }
    } catch (_e) {
      displayNotification('Error deleting user', {
        type: 'error',
        description: `An error occurred while removing ${member.email_address} from the workspace.`
      })
    }
  }

  const revokeInvite = async (member: Member) => {
    try {
      const response = await stytch.organization.members.delete(
        member.member_id
      )

      if (response.status_code === 200) {
        displayNotification('Invitation revoked', {
          type: 'success',
          description: `Invitation to ${member.email_address} was revoked.`
        })

        queryClient.invalidateQueries({
          queryKey: [TEAM_SETTINGS_QUERY_KEY]
        })
      }
    } catch (_e) {
      displayNotification('Error revoking invitation', {
        type: 'error',
        description: `An error occurred while revoking the invitation to ${member.email_address}.`
      })
    }
  }

  return {
    members,
    user,
    isLoading,
    isError,
    inviteMemberWithRoles: inviteMutator.mutate,
    isInvitePending: inviteMutator.isPending,
    updateMemberRoles: updateMemberRolesMutator.mutate,
    deleteMember,
    revokeInvite
  }
}
